/**
 * Componente Toast
 * Notificación individual con animaciones y diseño moderno
 */

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimesCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  Notification,
  NotificationType,
} from '../../contexts/NotificationContext';

interface ToastProps {
  notification: Notification;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animación de entrada
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    // Esperar a que termine la animación antes de remover
    setTimeout(() => {
      onClose(notification.id);
    }, 300);
  };

  const getToastStyles = (type: NotificationType) => {
    const baseStyles =
      'relative overflow-hidden rounded-xl shadow-xl border backdrop-blur-sm';

    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50/98 border-green-200 text-green-900`;
      case 'error':
        return `${baseStyles} bg-red-50/98 border-red-200 text-red-900`;
      case 'warning':
        return `${baseStyles} bg-amber-50/98 border-amber-200 text-amber-900`;
      case 'info':
        return `${baseStyles} bg-blue-50/98 border-blue-200 text-blue-900`;
      default:
        return `${baseStyles} bg-gray-50/98 border-gray-200 text-gray-900`;
    }
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return { icon: faCheckCircle, color: 'text-green-600' };
      case 'error':
        return { icon: faTimesCircle, color: 'text-red-600' };
      case 'warning':
        return { icon: faExclamationTriangle, color: 'text-amber-600' };
      case 'info':
        return { icon: faInfoCircle, color: 'text-blue-600' };
      default:
        return { icon: faInfoCircle, color: 'text-gray-600' };
    }
  };

  const { icon, color } = getIcon(notification.type);

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out mb-3 min-w-[320px] max-w-md
        ${
          isVisible && !isLeaving
            ? 'translate-y-0 opacity-100 scale-100'
            : '-translate-y-2 opacity-0 scale-95'
        }
        ${getToastStyles(notification.type)}
      `}
      role="alert"
      aria-live="polite"
    >
      {/* Barra de progreso */}
      {notification.duration && notification.duration > 0 && (
        <div className="absolute top-0 left-0 h-1 bg-black/5 w-full rounded-t-xl">
          <div
            className="h-full bg-current opacity-40 transition-all ease-linear rounded-t-xl"
            style={{
              animation: `shrink ${notification.duration}ms linear forwards`,
            }}
          />
        </div>
      )}

      <div className="flex items-start p-5">
        {/* Icono */}
        <div className={`flex-shrink-0 mr-4 ${color}`}>
          <FontAwesomeIcon icon={icon} className="text-lg" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm leading-5 mb-1">
            {notification.title}
          </h4>
          {notification.message && (
            <p className="text-sm opacity-80 leading-5">
              {notification.message}
            </p>
          )}

          {/* Acción opcional */}
          {notification.action && (
            <button
              onClick={notification.action.onClick}
              className="mt-2 text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-current focus:ring-opacity-50 rounded"
            >
              {notification.action.label}
            </button>
          )}
        </div>

        {/* Botón de cerrar */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 ml-4 p-1.5 rounded-full hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-current focus:ring-opacity-50 transition-colors duration-200"
          aria-label="Close notification"
        >
          <FontAwesomeIcon
            icon={faTimes}
            className="text-sm opacity-50 hover:opacity-80"
          />
        </button>
      </div>
    </div>
  );
};

export default Toast;
